import dayjs from 'dayjs'
import querystring from 'querystring'
import {
  Contribution,
  ContributionKeywordsParam,
  ContributionMessage,
  ContributionsQueryParam,
  ContributionUpdateParam,
} from 'src/models/Contribution'
import { filterUndefined } from 'src/utils/object'
import useSWR, { mutate, SWRConfiguration } from 'swr'
import { ApiClient, fetcher } from './client'
import {
  CONTRIBUTION_SEARCH_CONDITIONS,
  contributionSearchConditionsId,
  GET_CONTRIBUTIONS,
  GET_MY_CONTRIBUTIONS,
  getContribution,
  getMessagesFromContribution,
  NEW_CONTRIBUTION,
  POST_CONTRIBUTION,
  postMessageToContribution,
  putContribution,
  putContributionShareFb,
} from './endpoints'
import {
  ContributionMessageResponse,
  ContributionMessagesResponse,
  ContributionNewResponse,
  ContributionRequest,
  ContributionResponse,
  ContributionSearchConditionsData,
  ContributionsResponse,
  Contribution as ServerContribution,
} from './types/contribution'
// @ts-ignore
import { ShareDialogParams, ShareDialogResponse } from 'facebook-js-sdk'

export type ContibutionsData = {
  isLast: boolean
  contributions: Contribution[]
  availableCount: number
  contributionKeywords: ContributionKeywordsParam[]
  totalCount: number
  onePageDisplayItems: number
  keywordSearchCount: number
}
type ContributionNewData = {
  facebookAppId: string
}

export const toContribution = (data: ServerContribution): Contribution => ({
  id: data.id,
  buy: data.buy || '',
  sell: data.sell,
  detail: data.detail || '',
  userId: data.companyId,
  name: data.company.adminName || '',
  companyName: data.company.name || '',
  position: data.company.positionText || undefined,
  iconSrc: data.company.imageUrl?.medium?.url || data.company.imageUrl?.url,
  publishedAt: dayjs(data.publishAt),
  status: data.status,
  paidPlan: data.company.paidPlan,
  role: data.company.role,
  consultation: data.consultation || '',
  typeOfPurchase: data.typeOfPurchase,
  isBuy: data.isBuy,
  isConsultation: data.isConsultation,
  specialties: data.specialties,
  background: data.background,
  budget: data.budget,
  introductionTiming: data.introductionTiming,
  feedbackScore: data.company.feedbackScore,
  feedbackCount: data.company.feedbackCount,
})
const contributionsFetcher = async (url: string): Promise<ContibutionsData> => {
  const res: ContributionsResponse = await fetcher(url)
  return {
    isLast: res.isLast,
    contributions: res.contributions.map(toContribution),
    availableCount: res.contributionAvailableCount,
    contributionKeywords: res.contributionKeywords,
    totalCount: res.totalCount,
    onePageDisplayItems: res.onePageDisplayItems,
    keywordSearchCount: res.keywordSearchCount,
  }
}
const contributionFetcher = async (
  url: string,
): Promise<Contribution | undefined> => {
  const res: ContributionResponse = await fetcher(url)
  return res.contribution ? toContribution(res.contribution) : undefined
}
const toContributionsQuery = (param: ContributionsQueryParam) => {
  const query = querystring.stringify(
    filterUndefined({
      q: param.keyword,
      q_type: param.type,
      use_keyword: param.use_keyword,
      ['keyword_ids[]']: param['keyword_ids[]'],
      free_word: param.free_word,
      type_of_purchase: param.type_of_purchase,
      specialties: param.specialties,
      budget: param.budget,
      introduction_timing: param.introduction_timing,
    }),
  )
  return query ? `&${query}` : ''
}

const ContributionNewFetcher = async (
  url: string,
): Promise<ContributionNewData> => {
  const res: ContributionNewResponse = await fetcher(url)
  return {
    facebookAppId: res.koalaAppId,
  }
}
export const useContributions = (
  query: ContributionsQueryParam = {},
  page: number = 1,
  config: Partial<SWRConfiguration> = {},
) =>
  useSWR<ContibutionsData>(
    `${GET_CONTRIBUTIONS}?page=${page}${toContributionsQuery(query)}`,
    contributionsFetcher,
    config,
  )

export const fetchContributions = (
  query: ContributionsQueryParam = {},
  page: number = 1,
) =>
  contributionsFetcher(
    `${GET_CONTRIBUTIONS}?page=${page}${toContributionsQuery(query)}`,
  )

export const useContribution = (
  id: number,
  config: Partial<SWRConfiguration> = {},
) =>
  useSWR<Contribution | undefined>(
    getContribution(id),
    contributionFetcher,
    config,
  )

export const useMyContributions = (
  page: number,
  config: Partial<SWRConfiguration> = {},
) =>
  useSWR<ContibutionsData>(
    `${GET_MY_CONTRIBUTIONS}?page=${page}`,
    contributionsFetcher,
    config,
  )

export const fetchMyContributions = (page: number) =>
  contributionsFetcher(`${GET_MY_CONTRIBUTIONS}?page=${page}`)

export const useContributionNew = (config: Partial<SWRConfiguration> = {}) =>
  useSWR<ContributionNewData>(NEW_CONTRIBUTION, ContributionNewFetcher, config)

export const shareFbContribution = async (id: number) => {
  const body: typeof ShareDialogParams = {
    method: 'share',
    href: 'https://only-story.jp/chira-ceo/',
    display: 'popup',
    hashtag: '#ONLYSTORY',
  }
  return new Promise((resolve) => {
    FB.ui(body, (res: typeof ShareDialogResponse) =>
      resolve(
        !res?.error_code ? ApiClient.put(putContributionShareFb(id)) : res,
      ),
    )
  })
}

const createContributionOptimisticMutator = (
  data: ContibutionsData,
): ContibutionsData => ({
  ...data,
  availableCount: data.availableCount - 1,
})

export const createContribution = async (param: ContributionUpdateParam) => {
  const body = toContributionRequest(param)
  const res = await ApiClient.post(POST_CONTRIBUTION, body)
  if (!res.error && res.data)
    mutate(
      `${GET_CONTRIBUTIONS}?page=1`,
      createContributionOptimisticMutator,
      false,
    )
  return res
}

export const updateContribution = async (
  id: number,
  param: ContributionUpdateParam,
) => {
  const body = toContributionRequest(param)
  const res = await ApiClient.put(putContribution(id), body)
  if (!res.error && res.data) mutate(getContribution(id))
  return res
}

const toContributionRequest = (
  param: ContributionUpdateParam,
): ContributionRequest => ({
  contribution: {
    sell: param.sell ? param.sell : null,
    buy: param.typeOfPurchase === 'buy' ? param.buy : null,
    detail: param.detail ? param.detail : undefined,
    status: param.status ? param.status : 'publish',
    share_fb: param.isShareFb,
    type_of_purchase: param.typeOfPurchase,
    consultation:
      param.typeOfPurchase === 'consultation' ? param.consultation : null,
    from: 'web',
    specialties: param.specialties,
    background: param.background,
    budget: param.budget,
    introduction_timing: param.introductionTiming,
  },
})

export const sendMessage = async (id: number) => {
  const res = await ApiClient.post<ContributionMessageResponse>(
    postMessageToContribution(id),
  )
  return {
    ...res,
    data: {
      roomUrl: res.data?.contributionCommentCompany.roomUrl,
    },
  }
}

const toContributionMessages = (
  data: ContributionMessagesResponse,
): ContributionMessage[] =>
  data.contributionCommentCompanies.map((each) => ({
    userId: each.commentCompanyId,
    name: each.commentCompany.adminName || '',
    companyName: each.commentCompany.name || '',
    createdAt: dayjs(each.createdAt),
    roomUrl: each.roomUrl,
    ionSrc:
      each.commentCompany.imageUrl?.thumb?.url ||
      each.commentCompany.imageUrl?.url,
  }))

const contributionMessagesFetcher = async (url: string) => {
  const res: ContributionMessagesResponse = await fetcher(url)
  return res ? toContributionMessages(res) : []
}

export const useContributionMessages = (
  id: number,
  config: Partial<SWRConfiguration> = {},
) =>
  useSWR<ContributionMessage[]>(
    getMessagesFromContribution(id),
    contributionMessagesFetcher,
    config,
  )

export const createContributionSearchConditions = async (
  title: string,
  params: string,
) => {
  const body = {
    title: title,
    params: params,
  }
  const res = await ApiClient.post(CONTRIBUTION_SEARCH_CONDITIONS, body)
  return res
}

export const updateContributionSearchConditions = async (
  id: number,
  title: string,
  params: string,
) => {
  const body = {
    title: title,
    params: params,
  }
  const res = await ApiClient.put(contributionSearchConditionsId(id), body)
  return res
}

export const deleteContributionSearchConditions = async (id: number) => {
  const res = await ApiClient.delete(contributionSearchConditionsId(id))
  return res
}

const contributionSearchConditionsFetcher = async (
  url: string,
): Promise<ContributionSearchConditionsData[]> => {
  const res = await fetcher(url)
  return res
}

export const useContributionSearchConditions = (
  config: Partial<SWRConfiguration> = {},
) =>
  useSWR<ContributionSearchConditionsData[]>(
    CONTRIBUTION_SEARCH_CONDITIONS,
    contributionSearchConditionsFetcher,
    config,
  )

export const fetchContributionSearchConditions = () =>
  contributionSearchConditionsFetcher(CONTRIBUTION_SEARCH_CONDITIONS)
